import React, { lazy, Suspense } from 'react'
import { Route, Switch } from 'react-router-dom'

import { PrivateRoute } from '../components/private-route'
import { allPlatforms } from '../constants'
import CircularProgress from '../components/circular-progress'

// Retry dynamic imports to handle stale chunks after deployments.
// When a new deploy changes chunk hashes, cached HTML may reference old URLs that 404.
// This retries once, then reloads the page to fetch fresh HTML (max once per 5 min).
const RELOAD_KEY = 'lazyWithRetry_lastReload'
function lazyWithRetry(importFn: () => Promise<{ default: React.ComponentType<any> }>) { // eslint-disable-line @typescript-eslint/no-explicit-any
  return lazy(() =>
    importFn().catch(() =>
      importFn().catch((error) => {
        const lastReload = Number(sessionStorage.getItem(RELOAD_KEY) || '0')
        if (Date.now() - lastReload > 300_000) {
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
          window.location.reload()
          return new Promise(() => { /* never resolves — page is reloading */ })
        }
        throw error
      })
    )
  )
}

// Eagerly load the dashboard since it's the most common entry point
import DashboardPage from './DashboardPage'
import DashboardPageForUsers from './DashboardPageForUsers'

// Lazy load all other routes
const RequestLeavePage = lazyWithRetry(() => import('./Leaves/RequestLeavePage'))
const RequestToilPage = lazyWithRetry(() => import('./Toil/RequestToilPage'))
const EditRequestToilPage = lazyWithRetry(() => import('./Toil/EditRequestToilPage'))
const EditLeavePage = lazyWithRetry(() => import('./Leaves/EditLeavePage'))
const AddLeavePage = lazyWithRetry(() => import('./Leaves/AddLeavePage'))
const LeaveRequestsPage = lazyWithRetry(() => import('./Leaves/LeaveRequestsPage'))
const CalendarPage = lazyWithRetry(() => import('./CalendarPage'))
const WallchartPage = lazyWithRetry(() => import('./Wallchart'))
import TeamsFormPage from './Settings/Teams/TeamsFormPage'
import TeamPage from './Settings/Teams/TeamPage'
import TeamsPage from './Settings/Teams/TeamsPage'
import LocationFormPage from './Settings/Locations/LocationFormPage'
import LocationPage from './Settings/Locations/LocationPage'
import LocationsPage from './Settings/Locations/LocationsPage'
import LeavePoliciesPage from './Settings/LeavePoliciesPage'
import LeaveTypesPage from './Settings/LeaveTypesPage'
import HolidaysPage from './HolidaysPage'
import UserPage from './Users/UserPage'
import UsersPage from './Users/UsersPage'
import GeneralPage from './Settings/GeneralPage'
import CompanyLogsPage from './Reports/CompanyLogsPage'
import ScheduledReportFormPage from './Reports/ScheduledReports/ScheduledReportFormPage'
import ScheduledReportsPage from './Reports/ScheduledReports/ScheduledReportsPage'
import NotificationsPage from './Notifications/NotificationsPage'
import NotificationsFormPage from './Notifications/NotificationsFormPage'
import BillingPage from './Settings/BillingPage'
import AccountSettingsPage from './Settings/AccountSettings'
import MyProfilePage from './MyProfilePage'
import EnergyLevelPage from './EnergyLevelPage'
import NotFoundPage from './NotFoundPage'
import BotUpdatePermissionsPage from './BotUpdatePermissionsPage'
import ManageMicrosoftLicences from './Users/ManageMicrosoftLicences'
import ImportUsersPage from './Users/ImportUsersPage'
import AddonsPage from './AddonsPage'
import AddonFormPage from './Add-ons/AddonFormPage'
import AddonViewPage from './Add-ons/AddonViewPage'
import AddonViewDocumentationPage from './Add-ons/AddonViewDocumentationPage'
import ImportHolidaysPage from './Settings/Locations/ImportHolidaysPage'
import OutOfOffice from './OutOfOffice'
import ImportsPage from './ImportsPage'
const ImportPage = lazyWithRetry(() => import('./ImportPage'))
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import CalendarSyncPage from './CalendarSyncPage'
import AIAssistantPage from './AIAssistedOnboarding'
import AIAssistantConversations from './AIAssistantConversations'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import ReportsPage from './Reports'
import LeaveHistoryReportPage from './Report/LeaveHistoryReportPage'
import LeaveBalanceReportPage from './Report/LeaveBalanceReportPage'
import FeatureFlagsPage from './FeatureFlagsPage'
import MigrationPage from './MigrationPage'
import LeaveBalanceMonthlyReportPage from './Report/LeaveBalanceMonthlyReportPage'
const ImportEmailUsersPage = lazyWithRetry(() => import('./Users/ImportEmailUsersPage'))
import AddHolidayPage from './Settings/Locations/AddHolidayPage'
import LeavePoliciesOverviewPage from './Settings/LeavePoliciesOverviewPage'


interface IApp {
  match: {
    url: string
  }
}

const App = ({ match }: IApp): React.ReactElement => {
  return (
    <div className="main-content-wrapper">
      <Suspense fallback={<CircularProgress />}>
        <Switch>
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/leaves/request-leave`}
            component={RequestLeavePage}
          />
          <PrivateRoute
            roles={['User']}
            platform={allPlatforms}
            path={`${match.url}app/request-leave`}
            component={RequestLeavePage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/leaves/:id/edit`}
            component={EditLeavePage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver']}
            platform={allPlatforms}
            path={`${match.url}app/leaves/add-leave`}
            component={AddLeavePage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/leaves/leave-requests/:leaveRequestId`}
            component={LeaveRequestsPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/leaves/toil/:id/edit`}
            plan={SubscriptionPlanEnum.complete}
            featureFlag={FeatureFlagEnum.toil}
            component={EditRequestToilPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/leaves/request-toil`}
            plan={SubscriptionPlanEnum.complete}
            featureFlag={FeatureFlagEnum.toil}
            component={RequestToilPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/request-toil`}
            plan={SubscriptionPlanEnum.complete}
            featureFlag={FeatureFlagEnum.toil}
            component={RequestToilPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver']}
            platform={allPlatforms}
            path={`${match.url}app/dashboard`}
            component={DashboardPage}
          />
          <PrivateRoute
            roles={['User']}
            platform={allPlatforms}
            path={`${match.url}app/my-dashboard`}
            component={DashboardPageForUsers}
          />
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/calendar/sync/ical/create`}
            component={CalendarSyncPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/calendar/sync/ical/summary`}
            component={CalendarSyncPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/calendar/sync/google/connect`}
            component={CalendarSyncPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/calendar/sync/google/reconnect`}
            component={CalendarSyncPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/calendar/sync/google/create`}
            component={CalendarSyncPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/calendar/sync/google/update`}
            component={CalendarSyncPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/calendar/sync/outlook/connect`}
            component={CalendarSyncPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/calendar/sync/outlook/reconnect`}
            component={CalendarSyncPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/calendar/sync/outlook/create`}
            component={CalendarSyncPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/calendar/sync/outlook/update`}
            component={CalendarSyncPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/calendar/sync`}
            component={CalendarSyncPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/calendar`}
            component={CalendarPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver']}
            platform={allPlatforms}
            path={`${match.url}app/wallchart`}
            component={WallchartPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/settings/departments/create`}
            component={TeamsFormPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/settings/departments/:id/edit`}
            component={TeamsFormPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/settings/departments/:id`}
            component={TeamPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/settings/departments`}
            component={TeamsPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/settings/locations/create`}
            component={LocationFormPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/settings/locations/:id/edit`}
            component={LocationFormPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/settings/locations/:id/:type`}
            component={LocationPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/settings/location/:locationId/leave-policies/:leaveTypeId`}
            component={LeavePoliciesPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/settings/leave-policies`}
            component={LeavePoliciesOverviewPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/settings/locations`}
            component={LocationsPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/settings/leave-types`}
            component={LeaveTypesPage}
          />
          <PrivateRoute
            roles={['Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/holidays`}
            component={HolidaysPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/import-holidays/:id/:year?`}
            component={ImportHolidaysPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/holiday/:locationId/:holidayId?`}
            component={AddHolidayPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={['microsoft']}
            path={`${match.url}app/users/manage-licences`}
            component={ManageMicrosoftLicences}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={['microsoft', 'slack', 'google']}
            path={`${match.url}app/users/import`}
            component={ImportUsersPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={['email']}
            path={`${match.url}app/users/import-email-users`}
            component={ImportEmailUsersPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver']}
            platform={allPlatforms}
            path={`${match.url}app/users/:id`}
            component={UserPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver']}
            platform={allPlatforms}
            path={`${match.url}app/users`}
            component={UsersPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/settings/general`}
            component={GeneralPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/settings/personal`}
            component={AccountSettingsPage}
          />

          <PrivateRoute
            roles={['Admin', 'Approver']}
            platform={allPlatforms}
            path={`${match.url}app/notifications/:id/edit`}
            component={NotificationsFormPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver']}
            platform={allPlatforms}
            path={`${match.url}app/notifications/create`}
            component={NotificationsFormPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver']}
            platform={allPlatforms}
            path={`${match.url}app/notifications`}
            component={NotificationsPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver']}
            platform={allPlatforms}
            path={`${match.url}app/add-ons/:addonType/view/documentation`}
            component={AddonViewDocumentationPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver']}
            platform={allPlatforms}
            path={`${match.url}app/add-ons/:addonType/view`}
            component={AddonViewPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver']}
            platform={allPlatforms}
            path={`${match.url}app/add-ons/:addonType/create`}
            component={AddonFormPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver']}
            platform={allPlatforms}
            path={`${match.url}app/add-ons/:addonType/:id/edit`}
            component={AddonFormPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver']}
            platform={allPlatforms}
            path={`${match.url}app/add-ons`}
            component={AddonsPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/imports`}
            component={ImportsPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/import`}
            component={ImportPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/settings/billing`}
            component={BillingPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/reports/scheduled-reports/:id/edit`}
            component={ScheduledReportFormPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/reports/company-logs`}
            component={CompanyLogsPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/reports/scheduled-reports/create`}
            component={ScheduledReportFormPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/reports/scheduled-reports`}
            component={ScheduledReportsPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/reports/leave-history-report`}
            component={LeaveHistoryReportPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/reports/leave-balance-report`}
            component={LeaveBalanceReportPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/reports/leave-balance-monthly-report`}
            component={LeaveBalanceMonthlyReportPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/reports`}
            component={ReportsPage}
          />
          <PrivateRoute
            roles={['Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/my-profile`}
            component={MyProfilePage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/energy-level`}
            component={EnergyLevelPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={['slack']}
            path={`${match.url}app/bot-update-permissions`}
            component={BotUpdatePermissionsPage}
          />
          <PrivateRoute
            roles={['Admin', 'Approver', 'User']}
            platform={allPlatforms}
            path={`${match.url}app/out-of-office`}
            component={OutOfOffice}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/ai-assistant/:id`}
            component={AIAssistantPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/ai-assistant`}
            component={AIAssistantConversations}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/vt-ff`}
            component={FeatureFlagsPage}
          />
          <PrivateRoute
            roles={['Admin']}
            platform={allPlatforms}
            path={`${match.url}app/migration`}
            component={MigrationPage}
          />
          <Route
            component={NotFoundPage}
          />
        </Switch>
      </Suspense>
    </div>
  )
}

export default App
